import React, { useState } from 'react';

import { t as translate } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as EmptyData } from '../../../../../assets/icons/empty_box.svg';
import CardNE from '../../../../../shared/components/CardNE/CardNE';
import CardStatusNE from '../../../../../shared/components/CardStatusNE/CardStatusNE';
import CustomEmptyPage from '../../../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import SearchBarNE from '../../../../../shared/components/SearchBarNE/SearchBarNE';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import StyledMenu from '../../../../../shared/components/StyledMenu/StyledMenu';
import StyledTable from '../../../../../shared/components/StyledTable/StyledTable';
import TablePagination from '../../../../../shared/components/TablePaginationNE/TablePaginationNE';
import Tag from '../../../../../shared/components/Tag/Tag';
import { DEFAULT_PAGINATOR_OPTIONS, DEFAULT_PAGINATOR_STATE } from '../../../../../shared/constants/constants';
import { FILTER_IMAGES } from '../../../../../shared/constants/images';
import {
  REWARDS_HISTORY_DETAILS_FILTER,
  REWARDS_ORDER_DETAILS_STATUS_TAG_TYPES
} from '../../../../../shared/constants/rewards';
import {
  CLICK_ACTION,
  COLLABORATOR_ORDER_DROPDOWN,
  SECTION_REWARDS_HISTORY_DETAILS_REQUEST
} from '../../../../../shared/constants/tagManager';
import { IUserCompany } from '../../../../../shared/interfaces/businessData';
import {
  IRewardsHistoryDetails,
  IRewardsHistoryDetailsFilter,
  IRewardsOrderDetails
} from '../../../../../shared/interfaces/rewards';
import { Rewards } from '../../../../../shared/services/api/rewards/rewards';
import tagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_ORDER_HISTORY_DETAIL } from '../../../../../shared/translations/rewards';
import { GenericAsyncVoidFunction } from '../../../../../shared/types/genericTypes';
import { downloadFile, toLocaleNumberFormat, toTitleCase } from '../../../../../shared/utils/utils';

interface OrderDetailsTableProps {
  onSearch: GenericAsyncVoidFunction;
  company: IUserCompany;
  loading: boolean;
  hasFilter: boolean;
  order?: IRewardsHistoryDetails;
}

export const ORDER_DETAILS_COLUMNS = [
  {
    name: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.table.email,
    key: 'email',
    class: ''
  },
  {
    name: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.table.name,
    key: 'name',
    class: '',
    render: (orderDetail: IRewardsOrderDetails) => {
      return toTitleCase(orderDetail.name);
    }
  },
  {
    name: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.table.points,
    key: 'amount',
    class: 'table-hide-mobile table-align-right-tablet',
    render: (orderDetail: IRewardsOrderDetails) => {
      return toLocaleNumberFormat(orderDetail.amount, { style: 'currency', currency: orderDetail.currency });
    }
  },
  {
    name: TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.table.status,
    key: 'status',
    class: 'table-hide-mobile table-align-right-tablet',
    render: (orderDetail: IRewardsOrderDetails) => {
      return (
        <Tag
          text={translate(TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.status[orderDetail.status])}
          type={REWARDS_ORDER_DETAILS_STATUS_TAG_TYPES[orderDetail.status]}
        />
      );
    }
  },
  {
    name: '',
    key: 'actions',
    class: 'table-hide-tablet'
  }
];

const OrderDetailsTable = ({ onSearch, company, order, loading, hasFilter }: OrderDetailsTableProps) => {
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE);
  const [filterState, setFilterState] = useState<IRewardsHistoryDetailsFilter>(REWARDS_HISTORY_DETAILS_FILTER.all);
  const [actualPage, setActualPage] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const { t } = useTranslation();

  const paginatedOrderDetails = () => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    return order?.orderDetails ? order.orderDetails.slice(from, to) : undefined;
  };

  const changeOption = (filterOption: IRewardsHistoryDetailsFilter) => {
    setFilterState(filterOption);
    handleClose();
    tagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_ORDER_DROPDOWN, filterOption.gtmTag);
    fetchFiltereOrderDetails(searchValue, filterOption);
  };

  const handleSearch = (search: string) => {
    fetchFiltereOrderDetails(search, filterState);
  };

  const fetchFiltereOrderDetails = async (currentSearch: string, currentFilter: IRewardsHistoryDetailsFilter) => {
    const search = currentSearch ? currentSearch : undefined;
    await onSearch(search, currentFilter.status);
  };

  const downloadErrorsFile = async () => {
    if (!order?.orderInfo.id) {
      return;
    }
    const response = await Rewards.getOrderErrorFile(company.id, order.orderInfo.id);
    downloadFile(response.file);
  };

  const handleWrite = (search: string) => {
    setSearchValue(search);
  };

  const handleButtonClick = async (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const refreshSearch = () => fetchFiltereOrderDetails(searchValue, filterState);

  const hasDetailsData = () => order?.orderDetails && order.orderDetails.length > 0;

  const showError = () => !hasDetailsData() && !loading && !hasFilter;

  const optionMenu = [
    {
      label: t(REWARDS_HISTORY_DETAILS_FILTER.all.filter),
      action: () => changeOption(REWARDS_HISTORY_DETAILS_FILTER.all)
    },
    {
      label: t(REWARDS_HISTORY_DETAILS_FILTER.pending.filter),
      action: () => changeOption(REWARDS_HISTORY_DETAILS_FILTER.pending)
    },
    {
      label: t(REWARDS_HISTORY_DETAILS_FILTER.assigned.filter),
      action: () => changeOption(REWARDS_HISTORY_DETAILS_FILTER.assigned)
    },
    {
      label: t(REWARDS_HISTORY_DETAILS_FILTER.voided.filter),
      action: () => changeOption(REWARDS_HISTORY_DETAILS_FILTER.voided)
    }
  ];

  return (
    <>
      <CardNE className="justify-between gap-6" data-experience="new">
        <h3 className="ne-font-body-lg font-bold m-0">{t(TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.title)}</h3>

        {order?.orderInfo.errorsCount && (
          <CardStatusNE
            type="alert"
            message={
              <Trans
                i18nKey={TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.pendingErrors}
                components={{
                  u: (
                    <span
                      className="text-link regular"
                      onClick={downloadErrorsFile}
                      onKeyDown={downloadErrorsFile}
                    ></span>
                  )
                }}
                count={Number(order.orderInfo.errorsCount)}
                values={{ rows: order?.orderInfo.errorsCount }}
              />
            }
          />
        )}

        {showError() ? (
          <CustomEmptyPage
            title={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noData.title)}
            subtitle={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noData.description)}
            newExperience={true}
            buttonText={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noData.buttonText)}
            buttonAction={refreshSearch}
          />
        ) : (
          <>
            <div className="flex flex-row justify-between w-full">
              <SearchBarNE
                placeholder={t(TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.searchPlaceholder)}
                onSearch={handleSearch}
                placeholderSmall={t(TRANSLATION_ORDER_HISTORY_DETAIL.orderDetailsTable.searchMobile)}
                company={company}
                loading={loading}
                onWrite={handleWrite}
              />
              <StyledButtonNE
                loading={loading}
                disabled={loading}
                buttonType="secondary"
                buttonSize="sm"
                classes="w-auto self-center ml-2 whitespace-nowrap"
                action={handleButtonClick}
                image={FILTER_IMAGES}
              >
                {t(filterState.filter)}
              </StyledButtonNE>
            </div>
            {hasFilter && !hasDetailsData() && !loading ? (
              <CustomEmptyPage
                title={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noDataFilter.title)}
                subtitle={t(TRANSLATION_ORDER_HISTORY_DETAIL.error.noDataFilter.description)}
                newExperience={true}
                CustomImage={EmptyData}
              />
            ) : (
              <StyledTable columns={ORDER_DETAILS_COLUMNS} data={paginatedOrderDetails()} error={false} />
            )}
          </>
        )}

        <StyledMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          menuItems={optionMenu}
          anchorOrigin={undefined}
          transformOrigin={undefined}
        />
      </CardNE>
      <TablePagination
        rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS}
        rowsPerPage={paginatorState}
        count={order?.orderDetails ? order.orderDetails.length : 0}
        actualPage={actualPage}
        marginTop={24}
        onPageChange={setActualPage}
        onRowsPerPageChange={setPaginatorState}
        sectionPage={SECTION_REWARDS_HISTORY_DETAILS_REQUEST}
      />
    </>
  );
};

export default OrderDetailsTable;
